/**
 * Snackbar / toast notification Web Component.
 *
 * API (available once the element connects):
 *   window.GantSnackbar.show({ message, type, link, linkText, autoClose })
 *
 * @param {string}  message    — notification copy (required)
 * @param {string}  type       — 'default' | 'club' | 'sale' |
 *                               'status-success' | 'status-warning' | 'status-error'
 *                               Defaults to 'default'.
 * @param {string}  link       — optional URL for an inline link
 * @param {string}  linkText   — link label (required when link is set)
 * @param {boolean} autoClose  — true = 5 s timer + SVG ring (default); false = persistent
 *
 * Multiple calls queue and play sequentially.
 */

const SNACKBAR_TYPES = [
  'default',
  'club',
  'sale',
  'status-success',
  'status-warning',
  'status-error',
];

const AUTO_CLOSE_MS = 5000;
const EXIT_DURATION_MS = 200; /* must match CSS --animation-speed-slow */

/* Types that warrant an assertive (interruptive) screen-reader announcement */
const ASSERTIVE_TYPES = new Set(['status-warning', 'status-error']);

/*
 * Selectors for fixed/sticky bars that may occupy the bottom of the viewport
 * on mobile. Add new entries here if future components need to be avoided.
 */
const BOTTOM_BAR_SELECTORS = [
  /* No [data-stuck="true"] guard here — the bar uses CSS transform to slide
     in/out, so data-stuck doesn't reliably reflect visual presence.
     The rect.top >= window.innerHeight guard in #syncBottomGap() skips the
     bar when it is translated off-screen (translateY(100%)). */
  '.sticky-add-to-cart__bar',
  '.cart-page__summary',
];

/* Generous threshold to absorb env(safe-area-inset-bottom) on iPhone (34 px)
   plus small measurement jitter — anything tighter misses stuck elements. */
const VIEWPORT_BOTTOM_THRESHOLD = 50;

class GantSnackbar extends HTMLElement {
  #queue = [];
  #visible = false;
  #timer = null;
  #barObserver = null;

  connectedCallback() {
    this._snackbar = this.querySelector('.snackbar');
    this._message = this.querySelector('.snackbar__message');
    this._closeContent = this.querySelector('.snackbar__close-content');
    this._timerProgress = this.querySelector('.snackbar__timer-progress');
    this._srPolite = this.querySelector('[data-snackbar-polite]');
    this._srAssertive = this.querySelector('[data-snackbar-assertive]');

    this.querySelector('.snackbar__close').addEventListener('click', () => this.#dismiss());

    window.GantSnackbar = { show: (opts) => this.#enqueue(opts) };
    document.addEventListener('snackbar:show', (e) => this.#enqueue(e.detail || {}));

    this.#watchBottomBars();
  }

  disconnectedCallback() {
    this.#barObserver?.disconnect();
    this.#stopScrollSync();
  }

  /* ── Bottom-bar avoidance ──────────────────────────────────────────
   * Measures the top edge of any sticky/fixed element currently sitting
   * at the bottom of the viewport and sets --snackbar-bottom-gap on the
   * host element. The CSS bottom formula adds this to --spacing-large,
   * keeping the snackbar visually above whatever is blocking the bottom.
   *
   * Using getBoundingClientRect().top rather than offsetHeight means
   * env(safe-area-inset-bottom) is automatically included — the element's
   * own layout already accounts for it, so viewport-relative coordinates
   * give us the right clearance without double-counting.
   */
  #syncBottomGap() {
    if (window.innerWidth >= 1025) {
      this.style.removeProperty('--snackbar-bottom-gap');
      return;
    }

    let gap = 0;

    BOTTOM_BAR_SELECTORS.forEach((selector) => {
      const el = document.querySelector(selector);
      if (!el) return;
      const rect = el.getBoundingClientRect();
      /* Only count elements whose bottom edge is within VIEWPORT_BOTTOM_THRESHOLD
         of the viewport bottom. The generous threshold (50 px) absorbs
         env(safe-area-inset-bottom) on iPhone (34 px) — getBoundingClientRect
         returns layout-relative coords, so stuck elements on iPhone report
         rect.bottom ≈ window.innerHeight - 34 rather than exactly innerHeight. */
      if (window.innerHeight - rect.bottom > VIEWPORT_BOTTOM_THRESHOLD) return;
      /* Also skip elements that are fully below the viewport (not yet in view). */
      if (rect.top >= window.innerHeight) return;
      gap = Math.max(gap, window.innerHeight - rect.top);
    });

    if (gap > 0) {
      this.style.setProperty('--snackbar-bottom-gap', gap + 'px');
    } else {
      this.style.removeProperty('--snackbar-bottom-gap');
    }
  }

  /* Re-sync while a snackbar is visible so sticky bars that become stuck
     after the initial show() call (e.g. cart summary as user scrolls) are
     caught without a full re-render. */
  #startScrollSync() {
    if (this._scrollSyncBound) return;
    this._scrollSyncBound = () => this.#syncBottomGap();
    window.addEventListener('scroll', this._scrollSyncBound, { passive: true });
  }

  #stopScrollSync() {
    if (!this._scrollSyncBound) return;
    window.removeEventListener('scroll', this._scrollSyncBound);
    this._scrollSyncBound = null;
  }

  /* Watch the ATC bar's data-stuck attribute so the snackbar repositions
     immediately when the bar slides in or out (e.g. while visible). */
  #watchBottomBars() {
    const atcBar = document.querySelector('.sticky-add-to-cart__bar');
    if (!atcBar) return;

    this.#barObserver = new MutationObserver(() => this.#syncBottomGap());
    this.#barObserver.observe(atcBar, {
      attributes: true,
      attributeFilter: ['data-stuck'],
    });
  }

  #enqueue(opts) {
    this.#queue.push(opts);
    if (!this.#visible) this.#showNext();
  }

  #showNext() {
    if (!this.#queue.length) return;
    this.#show(this.#queue.shift());
  }

  #show({ message = '', type = 'default', link, linkText, autoClose = true }) {
    /* Recalculate bottom clearance before every show */
    this.#syncBottomGap();

    const snackbar = this._snackbar;

    /* Reset type modifier classes */
    SNACKBAR_TYPES.forEach((t) => snackbar.classList.remove(`snackbar--${t}`));
    snackbar.classList.add(`snackbar--${type}`);

    /* Set message text safely — no innerHTML */
    this._message.textContent = '';
    this._message.appendChild(document.createTextNode(message));
    if (link && linkText) {
      const space = document.createTextNode(' ');
      const a = document.createElement('a');
      a.className = 'snackbar__link';
      a.href = link;
      a.textContent = linkText;
      this._message.appendChild(space);
      this._message.appendChild(a);
    }

    /* Timer ring state */
    const showTimer = Boolean(autoClose);
    this._closeContent.dataset.timer = showTimer ? 'true' : 'false';

    /* Restart timer SVG animation */
    if (showTimer && this._timerProgress) {
      this._timerProgress.classList.remove('is-animating');
      void this._timerProgress.getBoundingClientRect();
      this._timerProgress.classList.add('is-animating');
    }

    /* Make visible — start scroll listener so sticky bars that become stuck
       after show() (e.g. cart summary as user scrolls) trigger a re-sync. */
    snackbar.setAttribute('aria-hidden', 'false');
    snackbar.classList.add('is-visible');
    this.#visible = true;
    this.#startScrollSync();

    /* Screen-reader announcement */
    const region = ASSERTIVE_TYPES.has(type) ? this._srAssertive : this._srPolite;
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = message;
    });

    /* Auto-dismiss */
    clearTimeout(this.#timer);
    if (autoClose) {
      this.#timer = setTimeout(() => this.#dismiss(), AUTO_CLOSE_MS);
    }
  }

  #dismiss() {
    clearTimeout(this.#timer);
    this.#stopScrollSync();
    const snackbar = this._snackbar;
    snackbar.classList.remove('is-visible');
    snackbar.setAttribute('aria-hidden', 'true');
    this.#visible = false;

    setTimeout(() => this.#showNext(), EXIT_DURATION_MS);
  }
}

if (!customElements.get('gant-snackbar')) {
  customElements.define('gant-snackbar', GantSnackbar);
}
